import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import TagManager from 'react-gtm-module'

import ScreenLoading from 'components/ScreenLoading'
import Sources from 'pages/Sources/Sources'
import DataLoad from 'pages/DataLoad/DataLoad'
import MayaMcp from 'pages/MayaMcp/MayaMcp'
import OrganizationsContext from 'containers/organizations/organizationsContext'
import { selectToken } from 'containers/authentication/authenticationSlice'
import { MCP_URL_COPIED_EVENT, mcpUrlCopiedKey } from 'constants/mcp'
import api from 'containers/api'

import './DataInsights.scss'

/**
 * "Data & Insights" — the merged Sources / ETL / Maya MCP screen. The tabs are a
 * stepper: 1 Integrations › 2 Data Sync › 3 Ask Maya, so the product pipeline
 * (connect data → sync it → ask Maya) reads as a flow instead of three unrelated
 * nav items. Tabs guide but never gate — every tab stays clickable at all times.
 *
 * Step completion signals:
 *  - Integrations: the org has at least one connected service account
 *  - Data Sync:    Organization.etl_runs (total_runs) >= 1 — same signal ShopifyConfirm polls
 *  - Ask Maya:     the user has copied the MCP URL (per-org localStorage flag set by CopyUrlBlock)
 *
 * Signals refresh on every tab switch, and total_runs is re-polled while the user
 * is watching an incomplete first sync on the Data Sync tab, so the step turns
 * green without leaving the page.
 */

type TabId = 'integrations' | 'sync' | 'ask-maya'

interface Step {
  id: TabId
  label: string
  done: boolean
}

interface DataInsightsProps {
  organizationSlug: string
}

// What "next" means for each step, phrased for the progress banner.
const STEP_HINTS: Record<TabId, string> = {
  integrations: 'connect a data source',
  sync: 'run your first data sync',
  'ask-maya': 'connect your AI assistant and start asking questions',
}

// Re-poll cadence for total_runs while watching an unfinished first sync.
const SYNC_WATCH_INTERVAL_MS = 10000

const readMcpCopied = (organizationId?: number): boolean => {
  try {
    if (localStorage.getItem(mcpUrlCopiedKey(organizationId)) === 'true') return true
    if (!organizationId) return localStorage.getItem(mcpUrlCopiedKey()) === 'true'
    // One-time migration: CopyUrlBlock writes the unscoped key when it renders
    // before the org record resolves (e.g. ShopifyConfirm right after purchase).
    // Adopt it into this org's key and delete it so a pre-resolution copy can't
    // mark the step done for every other org on the device. Known limitation:
    // on multi-org accounts the flag is adopted by whichever org's flow reads
    // it first, which may not be the org the copy was made for — acceptable for
    // a per-device UX nudge; a server-side flag would fix the attribution.
    if (localStorage.getItem(mcpUrlCopiedKey()) === 'true') {
      localStorage.setItem(mcpUrlCopiedKey(organizationId), 'true')
      localStorage.removeItem(mcpUrlCopiedKey())
      return true
    }
    return false
  } catch (e) {
    // Blocked storage (private mode / embedded contexts) — treat as not done.
    return false
  }
}

const DataInsights = ({ organizationSlug }: DataInsightsProps) => {
  const { tab } = useParams<{ tab?: string }>()
  const history = useHistory()
  const location = useLocation()
  const { currentOrganization, isLoadingOrganization } = useContext(OrganizationsContext)
  const token = useSelector(selectToken)

  const [sourcesCount, setSourcesCount] = useState<number | null>(null)
  const [totalRuns, setTotalRuns] = useState<number | null>(null)
  const [mcpCopied, setMcpCopied] = useState(false)
  // Fetch failures leave the value null (unknown) instead of asserting 0: a transient
  // API error must not tell a fully-configured org to "connect a data source". The
  // smart landing treats unknown as done, so on errors it falls through to a later
  // step rather than confidently pointing at step 1.
  const [sourcesFailed, setSourcesFailed] = useState(false)
  const [runsFailed, setRunsFailed] = useState(false)

  const organizationId = currentOrganization?.id
  const etlEnabled = !!currentOrganization?.is_etl_enabled
  // Signals wait for the real org record: is_etl_enabled and the org id both come
  // from it, and acting on the pre-load placeholder would mis-seed the flow state.
  const orgReady = !!organizationId && !isLoadingOrganization

  const fetchSourcesCount = useCallback(async () => {
    if (!orgReady || !token) return
    try {
      const accounts = await api.getConnectedServiceAccounts({
        token,
        organizationId,
      })
      setSourcesCount(accounts?.length ?? 0)
      setSourcesFailed(false)
    } catch (e) {
      setSourcesFailed(true)
    }
  }, [orgReady, organizationId, token])

  const fetchTotalRuns = useCallback(async () => {
    if (!orgReady || !token) return
    if (!etlEnabled) {
      // No Data Sync step for this org — seed 0 so the smart landing doesn't wait
      // on a signal that will never load.
      setTotalRuns(0)
      setRunsFailed(false)
      return
    }
    try {
      const settings = await api.getETLSettings({ organizationId, token })
      setTotalRuns(typeof settings?.total_runs === 'number' ? settings.total_runs : 0)
      setRunsFailed(false)
    } catch (e) {
      setRunsFailed(true)
    }
  }, [orgReady, organizationId, token, etlEnabled])

  // Load / refresh the completion signals on mount, org change, and tab switch.
  useEffect(() => {
    fetchSourcesCount()
    fetchTotalRuns()
  }, [fetchSourcesCount, fetchTotalRuns, tab])

  // The user watching their first sync finish live on the Data Sync tab should see
  // the step turn green without navigating away: re-poll total_runs until it lands.
  useEffect(() => {
    if (tab !== 'sync' || !etlEnabled || (totalRuns ?? 0) > 0) return
    const interval = setInterval(fetchTotalRuns, SYNC_WATCH_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [tab, etlEnabled, totalRuns, fetchTotalRuns])

  // Ask Maya completion is a per-org flag; re-read when the org resolves and when
  // a copy happens anywhere in the app (CopyUrlBlock fires the event).
  useEffect(() => {
    setMcpCopied(readMcpCopied(organizationId))
    const onCopied = () => setMcpCopied(readMcpCopied(organizationId))
    window.addEventListener(MCP_URL_COPIED_EVENT, onCopied)
    return () => window.removeEventListener(MCP_URL_COPIED_EVENT, onCopied)
  }, [organizationId])

  const steps: Step[] = useMemo(() => {
    const result: Step[] = [
      { id: 'integrations', label: 'Integrations', done: (sourcesCount ?? 0) > 0 },
    ]
    if (etlEnabled) {
      result.push({ id: 'sync', label: 'Data Sync', done: (totalRuns ?? 0) > 0 })
    }
    result.push({ id: 'ask-maya', label: 'Ask Maya', done: mcpCopied })
    return result
  }, [sourcesCount, totalRuns, mcpCopied, etlEnabled])

  const activeTab: TabId | null = steps.some((s) => s.id === tab) ? (tab as TabId) : null

  // Smart landing: /data-insights with no tab (or an unavailable one, e.g. "sync"
  // on an ETL-disabled org) opens the first incomplete step once signals are in.
  // A signal is "in" when it loaded OR its fetch failed; a failed (unknown) signal
  // counts as done here so an API blip lands the user on a later step instead of
  // confidently pointing a configured org at step 1. The query string is carried
  // forward for param-driven flows.
  useEffect(() => {
    if (activeTab || !orgReady) return
    if ((sourcesCount === null && !sourcesFailed) || (totalRuns === null && !runsFailed)) return
    const firstIncomplete = steps.find((s) => {
      if (s.id === 'integrations' && sourcesCount === null) return false
      if (s.id === 'sync' && totalRuns === null) return false
      return !s.done
    })
    const target = (firstIncomplete ?? steps[steps.length - 1]).id
    history.replace({
      pathname: `/organization/${organizationSlug}/data-insights/${target}`,
      search: location.search,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, orgReady, sourcesCount, totalRuns, sourcesFailed, runsFailed])

  // Fallback for the smart landing: if the completion signals never arrive
  // (falsy token, org never resolving), land on Integrations after a few
  // seconds instead of spinning indefinitely. Cleared as soon as a tab is
  // active (normal landing or direct deep link). Bails while the org record
  // is still loading — a deep link to /data-insights/sync has no `sync` step
  // until etlEnabled resolves, and a slow (>8s) org load must not bounce that
  // valid link to Integrations; the timer restarts when loading completes.
  useEffect(() => {
    if (activeTab || isLoadingOrganization) return
    const timeout = setTimeout(() => {
      history.replace({
        pathname: `/organization/${organizationSlug}/data-insights/integrations`,
        search: location.search,
      })
    }, 8000)
    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isLoadingOrganization])

  if (!activeTab) {
    return <ScreenLoading />
  }

  const doneCount = steps.filter((s) => s.done).length
  const nextStep = steps.find((s) => !s.done)
  const activeIndex = steps.findIndex((s) => s.id === activeTab)
  const followingStep = activeIndex >= 0 ? steps[activeIndex + 1] : undefined

  const goToTab = (id: TabId) => {
    TagManager.dataLayer({
      dataLayer: {
        event: `data_insights_tab_${id.replace(/-/g, '_')}`,
        is_custom_track: true,
      },
    })
    // Keep the query string, consistent with the smart landing. CONTRACT: any
    // handler consuming a one-shot param from the query string must strip it
    // after use (see useShopifyStoreSwitch) — tab switches carry the search
    // along, so an unstripped one-shot param would re-trigger on every remount
    // of its tab.
    history.push({
      pathname: `/organization/${organizationSlug}/data-insights/${id}`,
      search: location.search,
    })
  }

  return (
    <div className="data-insights">
      <div className="di-header">
        <h2>Data &amp; Insights</h2>
        <span className="subtitle-text">
          From your data to AI insights in {steps.length} steps.
        </span>
      </div>

      {nextStep && (
        <div className="di-banner">
          <span className="di-meter" aria-hidden="true">
            {steps.map((s) => (
              <i key={s.id} className={s.done ? 'on' : ''} />
            ))}
          </span>
          <span className="di-banner-text">
            {doneCount === 0
              ? `Welcome! Set up Maya in ${steps.length} steps — start here and ${STEP_HINTS[nextStep.id]}.`
              : `${doneCount} of ${steps.length} steps done — next: ${STEP_HINTS[nextStep.id]}.`}
          </span>
          {nextStep.id !== activeTab && (
            <button className="di-banner-cta" onClick={() => goToTab(nextStep.id)}>
              Go to {nextStep.label} →
            </button>
          )}
        </div>
      )}

      {/* Plain buttons in a stepper (not ARIA tabs — no roving focus is implemented);
          aria-current marks the active step for assistive tech. */}
      <div className="di-steps">
        {steps.map((step, i) => (
          <Fragment key={step.id}>
            {i > 0 && (
              <span className="di-chevron" aria-hidden="true">
                ›
              </span>
            )}
            <button
              aria-current={activeTab === step.id ? 'step' : undefined}
              className={`di-step ${activeTab === step.id ? 'active' : ''} ${
                step.done ? 'done' : ''
              }`}
              onClick={() => goToTab(step.id)}
            >
              <span className="di-step-num">{step.done ? '✓' : i + 1}</span>
              {step.label}
            </button>
          </Fragment>
        ))}
      </div>

      <div className="di-content">
        {activeTab === 'integrations' && <Sources embedded />}
        {activeTab === 'sync' && <DataLoad embedded />}
        {activeTab === 'ask-maya' && <MayaMcp embedded />}
      </div>

      {followingStep && (
        <div className="di-footer">
          <button className="di-next" onClick={() => goToTab(followingStep.id)}>
            Next: {followingStep.label} →
          </button>
        </div>
      )}
    </div>
  )
}

export default DataInsights
